import type { PlaycastMessageDisplayAddDisplayReply } from '@playcastdotio/Messaging';

export type ShadowDisplayAddDisplayResult =
  PlaycastMessageDisplayAddDisplayReply['message'];

type PendingReply = {
  resolve: (reply: ShadowDisplayAddDisplayResult) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

export class ShadowDisplayReplyWaiter {
  private readonly pending = new Map<string, PendingReply>();

  constructor(private readonly timeoutMs = 60_000) {}

  public wait(sessionId: string): Promise<ShadowDisplayAddDisplayResult> {
    if (!sessionId) throw new Error('ShadowDisplay reply requires a sessionId');
    if (this.pending.has(sessionId)) {
      throw new Error(`ShadowDisplay reply already pending for ${sessionId}`);
    }
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        this.finish(sessionId, {
          success: false,
          sessionId,
          error: `Timed out waiting for ShadowDisplay addDisplay reply for ${sessionId}`,
        });
      }, this.timeoutMs);
      this.pending.set(sessionId, { resolve, timeoutId });
    });
  }

  public resolve(reply: ShadowDisplayAddDisplayResult): boolean {
    if (!reply.sessionId) return false;
    return this.finish(reply.sessionId, reply);
  }

  public cancel(sessionId: string, reason = 'ShadowDisplay request cancelled'): boolean {
    return this.finish(sessionId, { success: false, sessionId, error: reason });
  }

  public cancelAll(reason = 'ShadowDisplay requests cancelled'): void {
    for (const sessionId of Array.from(this.pending.keys())) {
      this.cancel(sessionId, reason);
    }
  }

  public getPendingCount(): number {
    return this.pending.size;
  }

  private finish(sessionId: string, reply: ShadowDisplayAddDisplayResult): boolean {
    const pending = this.pending.get(sessionId);
    if (!pending) return false;
    this.pending.delete(sessionId);
    clearTimeout(pending.timeoutId);
    pending.resolve({ ...reply, sessionId });
    return true;
  }
}
